import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import Modal from '@common/modal';
import Checkbox from '@common/checkbox';
import Icon from '@common/assets';
import { useSearchJobTitle } from '@repository/calendar/useCalendar';
import { useJobGroupsWithTitles } from '@repository/codes/useCodes';
import { JobTitleSearchItemDto } from '@domain/rs/calendar/JobTitleSearchListRs';
import { CalendarJobTitleDto } from '@domain/rs/calendar/CalendarUserFilterRs';
import Colors from '@domain/constant/colors';
import useReactQueryDebounce from '@utils/hooks/useReactQueryDebounce';
import useClickAway from '@utils/hooks/useClickOutside';

import classnames from 'classnames/bind';
import styles from './jobGroupModal.module.scss';
const cx = classnames.bind(styles);

interface JobGroupModalProps {
  visible: boolean;
  onClose: () => void;
}

const JobGroupModal = ({ visible, onClose }: JobGroupModalProps) => {
  const [selectedJobGroupCode, setSelectedJobGroupCode] = useState<number>(1);
  //확인을 누르면 jobTitle에 값을 적용해야하기 때문에 copy본 사용
  const [latestJobTitleCode, setLatestJobTitleCode] = useState<CalendarJobTitleDto[]>([]);
  //검색메뉴 모달
  const searchMenuRef = useRef<HTMLDivElement>(null);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [jobTitleKeyword, setJobTitleKeyword] = useState('');
  const debouncedSearchQuery = useReactQueryDebounce(jobTitleKeyword, 500);
  const { data: jobTitleSearchList } = useSearchJobTitle(debouncedSearchQuery, !!debouncedSearchQuery);
  const { data: jobGroupsWithTitles } = useJobGroupsWithTitles();
  const { getValues } = useFormContext();
  const jobTitlesValue = getValues('jobTitles');
  const { field: jobTitlesField } = useController({
    name: 'jobTitles',
  });

  //직무 검색 함수
  const onChangeKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    setJobTitleKeyword(e.target.value);
  };

  //체크 여부 판단 함수
  const getChecked = (jobTitleCode: number) => latestJobTitleCode.some((item) => item.code === jobTitleCode);

  const onSelecteJobTitle = (selected: JobTitleSearchItemDto, isChecked: boolean) => {
    if (!isChecked) {
      const filteredList = latestJobTitleCode.filter((el) => el.code !== selected.jobTitleCode);
      setLatestJobTitleCode(filteredList);
    } else {
      const selectedItem = { code: selected.jobTitleCode, name: selected.jobTitleName };
      const tempList = [...latestJobTitleCode, selectedItem];
      //정렬 이유 : 직군·직무 버튼 표시시 제일 상단에 있는 직무 값 먼저 표시하기 위함
      tempList.sort((a, b) => a.code - b.code);
      setLatestJobTitleCode(tempList);
    }
  };

  const onResetSelectedJobTitles = () => setLatestJobTitleCode([]);

  const onDeleteJobTitle = (selected: CalendarJobTitleDto) => {
    const filteredList = latestJobTitleCode.filter((el) => el.code !== selected.code);
    setLatestJobTitleCode(filteredList);
  };

  const handleAddButton = () => {
    jobTitlesField.onChange(latestJobTitleCode);
    onClose();
  };

  const jobGroupEl = jobGroupsWithTitles?.map((el) => (
    <a
      key={el.code}
      className={cx('btnJobGroup', { on: selectedJobGroupCode === el.code })}
      role="button"
      onClick={() => setSelectedJobGroupCode(el.code)}
    >
      {el.name}
    </a>
  ));

  const searchMenuEl = jobTitleSearchList?.jobTitles.map((item) => {
    const labelText = `${item.jobGroupName} > ${item.jobTitleName}`;
    const isChecked = getChecked(item.jobTitleCode);
    return (
      <li className={cx('listItem')} key={item.jobTitleCode}>
        <Checkbox
          type={'square'}
          checked={isChecked}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSelecteJobTitle(item, e.target.checked)}
        >
          <span className={cx('labelText')}>{labelText}</span>
        </Checkbox>
      </li>
    );
  });

  const jobTitleEl = jobGroupsWithTitles
    ?.find((el) => el.code === selectedJobGroupCode)
    ?.jobTitles.map((item) => {
      const jobGroupName = jobGroupsWithTitles.find((el) => el.code === selectedJobGroupCode)?.name ?? '';
      const isChecked = getChecked(item.code);
      const addItem = { jobGroupName, jobTitleCode: item.code, jobTitleName: item.name };
      return (
        <div className={cx('jobSelectWrap')} key={item.code}>
          <div className={cx('jobSelectBox')}>
            <Checkbox
              type={'square'}
              checked={isChecked}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSelecteJobTitle(addItem, e.target.checked)}
            >
              <span className={cx('labelText')}>{item.name}</span>
            </Checkbox>
          </div>
        </div>
      );
    });

  const chipEl = latestJobTitleCode.map((el) => {
    return (
      <div className={cx('chip')} key={el.code}>
        {el.name}
        <a className={cx('btnDelete')} role="button" onClick={() => onDeleteJobTitle(el)}>
          <Icon name="closeLight" width={16} height={16} />
        </a>
      </div>
    );
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        onClose();
        return;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useClickAway(() => setIsVisibleModal(false), searchMenuRef, 'mousedown');

  useEffect(() => {
    if (visible) setLatestJobTitleCode(jobTitlesValue);
  }, [visible]);

  useEffect(() => {
    if (jobTitleKeyword && !!jobTitleSearchList?.jobTitles.length) setIsVisibleModal(true);
    if (!jobTitleKeyword || !jobTitleSearchList?.jobTitles.length) setIsVisibleModal(false);
    //debouncedSearchQuery로 dependencies설정시 반영이 안되는 문제가 있음. -> jobTitleKeyword사용
  }, [jobTitleKeyword, jobTitleSearchList]);

  return (
    <Modal visible={visible}>
      <div className={cx('jobGroupModalArea')}>
        <div className={cx('modalTitleArea')}>
          <em className={cx('modalTitle')}>직무 필터</em>
          <a className="btnClose" role="button" onClick={onClose}>
            <Icon name="closeLight" width={24} height={24} />
          </a>
        </div>

        {/* 직무 검색 영역 */}
        <div className={cx('searchArea')}>
          <div className={cx('searchInner')}>
            <Icon name="searchLight" width={24} height={24} fill={Colors.C_COOL_GRAY_70} />
            <input
              type="text"
              className={cx('input')}
              placeholder={'직무를 입력해 주세요.'}
              onChange={onChangeKeyword}
            />
          </div>

          {/* [D] 직무 검색 리스트 영역 */}
          {isVisibleModal && (
            <div className={cx('searchMenuArea')} ref={searchMenuRef}>
              <ul className={cx('searchMenuList')}>{searchMenuEl}</ul>
            </div>
          )}
        </div>

        {/* 직군, 직무 선택 영역 */}
        <div className={cx('selectItemArea')}>
          {/* 직군 선택 영역 */}
          <div className={cx('jobGroupSelectArea')}>
            <div className={cx('jobGroupSelectInner')}>{jobGroupEl}</div>
          </div>

          {/* 직무 선택 영역 */}
          <div className={cx('jobSelectArea')}>
            <div className={cx('jobSelectInner')}>{jobTitleEl}</div>
          </div>
        </div>

        {/* 선택된 직무 영역 */}
        <div className={cx('chipArea')}>{chipEl}</div>

        {/* 하단 버튼 영역 */}
        <div className={cx('btnArea')}>
          <a className={cx('btnReset')} role="button" onClick={onResetSelectedJobTitles}>
            <Icon name="resetLight" width={24} height={24} />
            선택 초기화
          </a>

          {/* [D] 필터 선택 안된 경우 disabled 처리 */}
          <button className={cx('btnApply')} type="button" onClick={handleAddButton}>
            추가
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default JobGroupModal;
